import { Discount, DiscountKind, DiscountValueType, MenuItem } from '../../types/api';
import type { Locale } from '../../locales';
import { formatPercentOff, formatTenge, formatTengeOff } from '../../utils/money';
import { pluralFor } from '../../utils/plural';
import { fill } from '../../utils/template';
import type { DiscountStrings } from './discountDraft';

/**
 * Подписи скидки в списке акций и в каталоге (ADR-026).
 *
 * Главное правило: у каждого вида своя единица. `discount` у тенге и комбо —
 * лишь эквивалент для сортировки и старых сборок, показывать его как размер
 * нельзя: «−8 %» вместо «−500 ₸» — неправда про скидку партнёра.
 */

/** Вид скидки; у ответов до ADR-026 поля нет — это `group`. */
export function kindOf(discount: Discount): DiscountKind {
  return discount.kind === 'combo' ? 'combo' : 'group';
}

/** Как задан размер; у ответов до ADR-026 поля нет — это `percent`. */
export function valueTypeOf(discount: Discount): DiscountValueType {
  return discount.value_type === 'amount' ? 'amount' : 'percent';
}

/** Выгода комбо по текущему меню, ₸. null — бэк не прислал цен. */
export function comboBenefit(discount: Discount): number | null {
  if (discount.regular_price == null || discount.bundle_price == null) return null;

  return discount.regular_price - discount.bundle_price;
}

/**
 * Комбо, которое гость не видит: цены в меню снизили после сохранения, и
 * набор перестал быть дешевле своих позиций (ADR-026 §3).
 */
export function isComboWithoutSaving(discount: Discount): boolean {
  const benefit = comboBenefit(discount);

  return kindOf(discount) === 'combo' && benefit != null && benefit <= 0;
}

/** Состав комбо одной строкой: «Кальян + Чай × 2». */
export function comboComposition(discount: Discount): string {
  return (discount.combo_items ?? [])
    .map((line) => (line.quantity > 1 ? `${line.name} × ${line.quantity}` : line.name))
    .join(' + ');
}

export interface SizeLabel {
  /** «−20 %», «−500 ₸», «5 000 ₸». */
  main: string;
  /** Вторая строка у комбо: «вместо 6 500 ₸» или «нет выгоды». */
  sub?: string;
  /** Комбо без выгоды гость не видит — подпись другого тона. */
  warn?: boolean;
}

/** Размер скидки в строке списка. */
export function discountSizeLabel(discount: Discount, strings: DiscountStrings): SizeLabel {
  if (kindOf(discount) === 'combo') {
    const bundle = discount.bundle_price;
    const regular = discount.regular_price;
    const main = bundle != null ? formatTenge(bundle) : '—';

    if (bundle == null || regular == null) return { main };
    if (regular <= bundle) return { main, sub: strings.comboNoSaving, warn: true };

    return { main, sub: fill(strings.comboInstead, { price: formatTenge(regular) }) };
  }

  if (valueTypeOf(discount) === 'amount') {
    return { main: discount.amount != null ? formatTengeOff(discount.amount) : '—' };
  }

  return { main: formatPercentOff(discount.discount) };
}

/** То же одной строкой: «5 000 ₸ вместо 6 500 ₸». */
export function discountSizeText(discount: Discount, strings: DiscountStrings): string {
  const label = discountSizeLabel(discount, strings);

  return label.sub ? `${label.main} ${label.sub}` : label.main;
}

/** На что действует: раздел, «3 позиции» или состав комбо. */
export function discountScope(discount: Discount, strings: DiscountStrings, locale: Locale): string {
  if (kindOf(discount) === 'combo') return comboComposition(discount) || strings.scopeNoItems;

  if (discount.discount_type === 'category' || discount.menu_category) {
    const name = discount.menu_category?.name;

    return name ? fill(strings.scopeCategory, { name }) : strings.scopeCategoryUnnamed;
  }

  const count = discount.menu_items?.length ?? discount.menu_item_ids?.length ?? 0;
  if (count === 0) return strings.scopeNoItems;

  const word = pluralFor(locale, count, {
    one: strings.scopeItemOne,
    few: strings.scopeItemFew,
    many: strings.scopeItemMany,
  });

  return `${count} ${word}`;
}

/** Что это за скидка — подпись, когда заголовком строки стал охват. */
export function discountCaption(discount: Discount, strings: DiscountStrings): string {
  if (kindOf(discount) === 'combo') return strings.subtitleCombo;

  return discount.discount_type === 'category' || discount.menu_category
    ? strings.subtitleCategory
    : strings.subtitleItems;
}

/** Ключ «по размеру»: сначала проценты, потом тенге, потом комбо. */
function sizeKey(discount: Discount): [number, number] {
  if (kindOf(discount) === 'combo') return [2, comboBenefit(discount) ?? 0];
  if (valueTypeOf(discount) === 'amount') return [1, discount.amount ?? 0];

  return [0, discount.discount];
}

/**
 * Сортировка «по размеру скидки». Единицы не смешиваются: −500 ₸ не больше
 * и не меньше −20 %, поэтому внутри каждого вида — по убыванию, а виды идут
 * друг за другом.
 */
export function compareBySize(a: Discount, b: Discount): number {
  const [rankA, valueA] = sizeKey(a);
  const [rankB, valueB] = sizeKey(b);

  return rankA - rankB || valueB - valueA;
}

/**
 * Скидка позиции в каталоге: «−500 ₸» у тенге, «−20 %» у процента.
 * null — скидки нет. Без `best_discount_value_type` (ответ до ADR-026)
 * скидка считается процентной, как раньше.
 */
export function itemDiscountLabel(item: MenuItem): string | null {
  const percent = item.best_discount_percent ?? 0;
  const valueType = item.best_discount_value_type ?? (percent > 0 ? 'percent' : null);

  if (valueType === 'amount') {
    if (item.price_with_discount == null) return null;
    const off = item.price - item.price_with_discount;

    return off > 0 ? formatTengeOff(off) : null;
  }

  return valueType === 'percent' && percent > 0 ? formatPercentOff(percent) : null;
}
